import { scrollToId } from "../../lib/smooth";
import { useLanguage } from "../../i18n/LanguageContext";

const SOLUTION_LINKS = [
  { label: "Federations", href: "/solutions/federations" },
  { label: "Academies & Clubs", href: "/solutions/academies" },
  { label: "Schools & Universities", href: "/solutions/schools" },
  { label: "Venues & Stadiums", href: "/solutions/venues" },
  { label: "Government", href: "/solutions/government" },
  { label: "Event Workforce", href: "/solutions/event-workforce" },
];

const GroupTitle = ({ children }) => (
  <p className="flex items-center gap-3 font-mono text-[10px] tracking-[0.28em] uppercase text-white/35">
    <span className="h-px w-5 bg-kn-cyan/50" />
    {children}
  </p>
);

const LinkRow = ({ children, ...props }) => (
  <button
    {...props}
    className="block w-full text-left text-[13px] text-white/55 transition-all duration-300 hover:translate-x-1 hover:text-kn-cyan"
  >
    {children}
  </button>
);

export const Footer = () => {
  const { t } = useLanguage();
  const EXPLORE = [...t.nav.items, { label: t.nav.contact, href: "#contact" }];

  return (
    <footer className="bg-kn-navy2" data-testid="site-footer">
      <div className="mx-auto max-w-7xl px-6 pb-10 pt-16 lg:px-10 lg:pt-20">
        <div className="grid gap-12 lg:grid-cols-12">
          {/* brand + contact */}
          <div className="lg:col-span-4">
            <div className="flex items-center gap-3">
              <img src="/logo-white-wm.png" alt="CYRA" className="h-9 w-auto" />
              <span className="h-5 w-px bg-white/25" />
              <span className="font-serif text-xl italic leading-none text-kn-cyan">Krida</span>
            </div>
            <p className="mt-3 text-sm text-white/50">{t.footer.tagline}</p>
            <p className="mt-6 font-mono text-[10px] tracking-[0.3em] uppercase text-white/30">
              {t.footer.powered}
            </p>
            <ul className="mt-8 space-y-2.5 font-mono text-[11px] uppercase tracking-[0.18em] text-white/45">
              <li>
                <a href="https://www.krida.me" target="_blank" rel="noopener noreferrer" data-testid="footer-website-link" className="transition-colors duration-300 hover:text-kn-cyan">www.krida.me</a>
              </li>
              <li>
                <a href="mailto:hello@krida.me" data-testid="footer-email-link" className="transition-colors duration-300 hover:text-kn-cyan">hello@krida.me</a>
              </li>
              <li>Singapore · Malaysia · India</li>
            </ul>
          </div>

          {/* link groups */}
          <div className="grid grid-cols-2 gap-10 sm:grid-cols-3 lg:col-span-8 lg:pl-8">
            <div data-testid="footer-group-explore">
              <GroupTitle>Explore</GroupTitle>
              <ul className="mt-5 space-y-3">
                {EXPLORE.map((l) => (
                  <li key={l.href}>
                    <LinkRow data-testid={`footer-link-${l.href.slice(1)}`} onClick={() => scrollToId(l.href)}>
                      {l.label}
                    </LinkRow>
                  </li>
                ))}
              </ul>
            </div>
            <div data-testid="footer-group-solutions">
              <GroupTitle>Solutions</GroupTitle>
              <ul className="mt-5 space-y-3">
                {SOLUTION_LINKS.map((l) => (
                  <li key={l.href}>
                    <a
                      href={l.href}
                      data-testid={`footer-link-${l.href.split("/").pop()}`}
                      className="block text-[13px] text-white/55 transition-all duration-300 hover:translate-x-1 hover:text-kn-cyan"
                    >
                      {l.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div data-testid="footer-group-access">
              <GroupTitle>Access</GroupTitle>
              <ul className="mt-5 space-y-3">
                <li>
                  <LinkRow data-testid="footer-link-join" onClick={() => scrollToId("#join")}>Join Krida</LinkRow>
                </li>
                <li>
                  <a href="/admin" data-testid="footer-link-signin" className="block text-[13px] text-white/55 transition-all duration-300 hover:translate-x-1 hover:text-kn-cyan">Sign In</a>
                </li>
                <li>
                  <a href="/privacy" data-testid="footer-link-privacy" className="block text-[13px] text-white/55 transition-all duration-300 hover:translate-x-1 hover:text-kn-cyan">Privacy</a>
                </li>
                <li>
                  <a href="/terms" data-testid="footer-link-terms" className="block text-[13px] text-white/55 transition-all duration-300 hover:translate-x-1 hover:text-kn-cyan">Terms</a>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="mt-16 flex flex-col items-center justify-between gap-3 border-t border-white/10 pt-8 font-mono text-[10px] tracking-[0.2em] uppercase text-white/25 sm:flex-row">
          <span>{t.footer.rights}</span>
          <span>{t.footer.motto}</span>
        </div>
      </div>
    </footer>
  );
};
